import {
    ANONYMOUS_PRESENCE_DWELL_MS,
    ANONYMOUS_PRESENCE_VISIBLE_RATIO,
    anonymousPresenceResultIsTerminal,
    anonymousPresenceThresholdForHeight,
    qualifiesAnonymousPresence,
} from 'lib/anonymousPresencePolicy'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
export const ANONYMOUS_PRESENCE_QUALIFICATION_PATH = '/api/v1/anonymous_presence/qualify'
const activeStops = new Set<() => void>()
let trackingComplete = false

function stopAllTracking() {
    trackingComplete = true
    Array.from(activeStops).forEach(stop => stop())
    activeStops.clear()
}

async function postQualification(answerId: string): Promise<string | null> {
    const send = async () => {
        const response = await fetch(ANONYMOUS_PRESENCE_QUALIFICATION_PATH, {
            method: 'POST',
            credentials: 'same-origin',
            keepalive: true,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-Token': window.gon?.formToken || '',
            },
            body: JSON.stringify({
                answer_id: answerId,
                visible_ms: ANONYMOUS_PRESENCE_DWELL_MS,
            }),
        })
        if (!response.ok && response.status !== 202) return null

        const payload = await response.json()
        return typeof payload?.result === 'string' ? payload.result : null
    }

    let result = await send()
    if (result === 'retry_with_cookie') {
        result = await send()
        if (result === 'retry_with_cookie') result = 'cookies_required'
    }
    return result
}

export function observeAnonymousAnswerPresence(
    element: HTMLElement | null,
    answerId?: string | null,
): () => void {
    if (
        !element ||
        !answerId ||
        !UUID_RE.test(answerId) ||
        trackingComplete ||
        window.gon?.currentUser ||
        typeof IntersectionObserver === 'undefined'
    ) {
        return () => {}
    }

    let stopped = false
    let dwellTimer: number | null = null
    let isQualifying = false
    let requestStarted = false
    let observer: IntersectionObserver | null = null
    let resizeObserver: ResizeObserver | null = null

    const clearDwell = () => {
        if (dwellTimer == null) return
        window.clearTimeout(dwellTimer)
        dwellTimer = null
    }

    const stop = () => {
        if (stopped) return
        stopped = true
        clearDwell()
        observer?.disconnect()
        resizeObserver?.disconnect()
        document.removeEventListener('visibilitychange', handleVisibilityChange)
        activeStops.delete(stop)
    }

    const qualify = async () => {
        if (requestStarted || stopped) return
        requestStarted = true
        try {
            const result = await postQualification(answerId)
            if (anonymousPresenceResultIsTerminal(result)) stopAllTracking()
        } catch {
            // A later mounted/visible post may retry. Rewards never depend on this request succeeding.
        } finally {
            stop()
        }
    }

    const startDwell = () => {
        if (dwellTimer != null || requestStarted) return
        dwellTimer = window.setTimeout(() => {
            dwellTimer = null
            if (!isQualifying || document.visibilityState !== 'visible') return

            const checkVisibility = (element as HTMLElement & { checkVisibility?: () => boolean })
                .checkVisibility
            if (typeof checkVisibility === 'function' && !checkVisibility.call(element)) return
            void qualify()
        }, ANONYMOUS_PRESENCE_DWELL_MS)
    }

    const handleEntries: IntersectionObserverCallback = entries => {
        const entry = entries[entries.length - 1]
        if (!entry) return

        isQualifying = qualifiesAnonymousPresence(
            entry.isIntersecting,
            entry.intersectionRatio,
            entry.intersectionRect?.height || 0,
        )
        if (isQualifying && document.visibilityState === 'visible') startDwell()
        else clearDwell()
    }

    let visibilityThreshold = -1
    const observeAtHeight = (height: number) => {
        const nextThreshold = anonymousPresenceThresholdForHeight(height)
        if (Math.abs(nextThreshold - visibilityThreshold) < 0.001) return

        visibilityThreshold = nextThreshold
        isQualifying = false
        clearDwell()
        observer?.disconnect()
        observer = new IntersectionObserver(handleEntries, {
            threshold:
                nextThreshold === ANONYMOUS_PRESENCE_VISIBLE_RATIO
                    ? [0, ANONYMOUS_PRESENCE_VISIBLE_RATIO]
                    : [0, nextThreshold, ANONYMOUS_PRESENCE_VISIBLE_RATIO],
        })
        observer.observe(element)
    }

    function handleVisibilityChange() {
        if (document.visibilityState === 'visible' && isQualifying) startDwell()
        else clearDwell()
    }

    activeStops.add(stop)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    if (typeof ResizeObserver === 'undefined') {
        observeAtHeight(element.getBoundingClientRect().height || element.offsetHeight)
    } else {
        observeAtHeight(0)
        resizeObserver = new ResizeObserver(entries => {
            const entry = entries[entries.length - 1]
            if (entry) observeAtHeight(entry.contentRect.height)
        })
        resizeObserver.observe(element)
    }

    return stop
}

export function resetAnonymousPresenceTrackingForTests() {
    Array.from(activeStops).forEach(stop => stop())
    activeStops.clear()
    trackingComplete = false
}
